import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import type { FindPaymentsRequestParams } from '~domains/commerce/transactions/entities/payment';

import { useMerchantsListQuery } from '~platform/entities/merchant';

import { ampli } from '~shared/analytics';
import type { DataTableSorting } from '~shared/ui/data-table';
import { useColumnVisibility } from '~shared/ui/data-table';

import {
  getDefaultPaymentsFilter,
  persistPaymentsFilters,
  restorePaymentsFilters,
  type PaymentsFiltersValue,
} from './payments-filters';
import { usePaymentsTableDataQuery } from './payments-table-queries';

import { ALL_COLUMN_IDS } from '../lib/columns';
import { filtersToParams, sortToParams } from '../lib/search-params-converters';
import { defaultPagination } from '../lib/table-options';

const COLUMN_VISIBILITY_STORAGE_KEY = 'payments-table:column-visibility';

const MIN_MERCHANTS_TO_FILTER = 2;

export function usePaymentsTable() {
  const methods = useForm<PaymentsFiltersValue>({
    defaultValues: getDefaultPaymentsFilter(),
  });

  const [appliedFilters, setAppliedFilters] = useState<PaymentsFiltersValue>();
  const [pagination, setPagination] = useState(defaultPagination);
  const [sorting, setSorting] = useState<DataTableSorting>();
  const { visibleColumns, setVisibleColumns, columnVisibility } =
    useColumnVisibility(ALL_COLUMN_IDS, {
      storageKey: COLUMN_VISIBILITY_STORAGE_KEY,
    });

  const {
    data: merchantsList,
    isError: isMerchantsError,
    isSuccess: isMerchantsSuccess,
  } = useMerchantsListQuery();

  const trackFilterApplied = useCallback((filters: PaymentsFiltersValue) => {
    const { startDate, endDate } = filters.period ?? {};

    ampli.paymentsScreenFilterApplied({
      filter_period: startDate && endDate ? `${startDate}-${endDate}` : '',
      filter_chosenMerchantsCount: filters.merchantIds.length,
      filter_statuses: filters.statuses,
    });
  }, []);

  const resetPage = () =>
    setPagination((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));

  const applyFilters = useCallback(() => {
    const filters = methods.getValues();
    setAppliedFilters(filters);
    persistPaymentsFilters(filters);
    trackFilterApplied(filters);
    resetPage();
  }, [methods, trackFilterApplied]);

  const resetFilters = useCallback(() => {
    const defaults = getDefaultPaymentsFilter();
    methods.reset(defaults);
    setAppliedFilters(defaults);
    persistPaymentsFilters(defaults);
    trackFilterApplied(defaults);
    resetPage();
  }, [methods, trackFilterApplied]);

  const handleSortingChange = (newSorting: DataTableSorting) => {
    setSorting(newSorting);
    resetPage();
  };

  const isHydratedRef = useRef(false);
  useEffect(() => {
    if (isHydratedRef.current || !isMerchantsSuccess) {
      return;
    }

    isHydratedRef.current = true;
    const restored = restorePaymentsFilters(merchantsList);
    methods.reset(restored);
    setAppliedFilters(restored);
    persistPaymentsFilters(restored);
    trackFilterApplied(restored);
  }, [isMerchantsSuccess, merchantsList, methods, trackFilterApplied]);

  useEffect(() => {
    if (isMerchantsError || isMerchantsSuccess) {
      ampli.paymentsScreenShown({ merchantsCount: merchantsList?.length });
    }
  }, [isMerchantsError, isMerchantsSuccess, merchantsList]);

  const searchParams = useMemo<FindPaymentsRequestParams | undefined>(
    () =>
      appliedFilters
        ? {
            page: pagination.page,
            perPage: pagination.perPage,
            sort: sortToParams(sorting),
            filter: filtersToParams(appliedFilters),
          }
        : undefined,
    [appliedFilters, pagination, sorting]
  );

  const { data, isLoading } = usePaymentsTableDataQuery(searchParams);
  const payments = data?.payments ?? [];
  const total = data?.total ?? 0;

  const isHolding = isMerchantsSuccess
    ? merchantsList.some((merchant) => merchant.holding)
    : false;
  const hideMerchantFilter =
    !isMerchantsSuccess ||
    merchantsList.length < MIN_MERCHANTS_TO_FILTER ||
    isHolding;

  const clearSelectFilters = useCallback(() => {
    methods.setValue('statuses', []);
    methods.setValue('merchantIds', []);
  }, [methods]);

  const activeFiltersCount =
    (appliedFilters?.statuses.length ? 1 : 0) +
    (!hideMerchantFilter && appliedFilters?.merchantIds.length ? 1 : 0);

  return {
    methods,
    payments,
    total,
    isLoading,
    pagination,
    sorting,
    searchParams,
    hideMerchantFilter,
    applyFilters,
    resetFilters,
    clearSelectFilters,
    activeFiltersCount,
    onSortingChange: handleSortingChange,
    visibleColumns,
    onVisibleColumnsChange: setVisibleColumns,
    columnVisibility,
    onPageChange: (page: number) =>
      setPagination((prev) => ({ ...prev, page })),
    onPageSizeChange: (perPage: number) => setPagination({ page: 0, perPage }),
  };
}
